import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Statistic,
  Steps,
  Typography,
} from "antd";
import { useState } from "react";
import AppConfig from "utils/Config";

const { Title, Text } = Typography;
const { Option } = Select;

const SignUp = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  const schemePrices = {
    chemistry: 67500,
    hematology: 67500,
    hcg: 20000,
    hbs_ag: 20000,
    tb_microscopy: 30000,
    mp_microscopy: 30000,
    hiv_serology: 30000,
    cd4: 67500,
    hcv: 20000,
    h_pylori: 20000,
    malaria_rapid: 20000,
    sars_cov_2: 150000,
    immuno_assay_biorad: 1212200,
    chemistry_biorad: 868000,
    haematology_biorad: 1212200,
    courier_biorad: 50000,
    courier: 50000,
  };

  const steps = [
    {
      title: "Laboratory Information",
      icon: <BankOutlined />,
    },
    {
      title: "Scheme Selection",
      icon: <ExperimentOutlined />,
    },
    {
      title: "Review & Submit",
      icon: <CheckCircleOutlined />,
    },
  ];

  const handleSchemeChange = (schemes) => {
    setSelectedSchemes(schemes);
    const total = schemes.reduce(
      (sum, scheme) => sum + (schemePrices[scheme] || 0),
      0
    );
    setTotalAmount(total);
  };

  const handleSubmit = async (values) => {
    try {
      const enrollmentData = {
        laboratory: values.laboratory_id, // if existing lab
        laboratory_data: {
          // if new lab
          laboratory_name: values.laboratory_name,
          pml_number: values.pml_number,
          cac_registration_number: values.cac_registration_number,
          physical_organization: values.physical_organization,
          physical_city: values.physical_city,
          physical_postcode: values.physical_postcode || "+234",
          physical_country: values.physical_country || "Nigeria",
          postal_organization: values.postal_organization,
          postal_city: values.postal_city,
          postal_postcode: values.postal_postcode || "+234",
          postal_country: values.postal_country || "Nigeria",
          laboratory_telephone: values.laboratory_telephone,
          laboratory_fax: values.laboratory_fax,
          laboratory_email: values.laboratory_email,
          laboratory_level: values.laboratory_level,
          laboratory_type: values.laboratory_type,
        },
        selected_schemes: selectedSchemes,
        is_international: values.is_international || false,
      };

      // API call to save enrollment
      const response = await fetch("/api/neqal-enrollments/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(enrollmentData),
      });

      if (response.ok) {
        message.success("Enrollment submitted successfully!");
        form.resetFields();
        setCurrentStep(0);
        setSelectedSchemes([]);
        setTotalAmount(0);
      } else {
        message.error("Failed to submit enrollment. Please try again.");
      }
    } catch (error) {
      message.error("An error occurred. Please try again.");
    }
  };

  const renderLaboratoryInfo = () => (
    <Card title="1.0 LABORATORY INFORMATION" style={{ marginBottom: 24 }}>
      {/* Basic Information - 3 fields per row */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="laboratory_name"
            label="1.1 Name of Laboratory"
            rules={[
              { required: true, message: "Please enter laboratory name" },
            ]}
          >
            <Input placeholder="Enter laboratory name" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="pml_number"
            label="1.2 PML Number"
            rules={[{ required: true, message: "Please enter PML number" }]}
          >
            <Input placeholder="Enter PML number" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="cac_registration_number"
            label="1.3 CAC Registration Number"
          >
            <Input placeholder="Enter CAC number" />
          </Form.Item>
        </Col>
      </Row>

      <Divider>1.4 Laboratory Physical Address</Divider>

      {/* Physical Address - 3 fields per row */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="physical_organization"
            label="Organization Name"
            rules={[
              { required: true, message: "Please enter organization name" },
            ]}
          >
            <Input placeholder="Enter organization name" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="physical_city"
            label="City"
            rules={[{ required: true, message: "Please enter city" }]}
          >
            <Input placeholder="Enter city" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="physical_postcode" label="Postcode">
            <Input placeholder="+234" defaultValue="+234" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="physical_country" label="Country">
            <Input placeholder="Nigeria" defaultValue="Nigeria" />
          </Form.Item>
        </Col>
      </Row>

      <Divider>1.5 Laboratory Postal Address</Divider>

      {/* Postal Address - 3 fields per row */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="postal_organization" label="Organization Name">
            <Input placeholder="Enter organization name" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="postal_city" label="City">
            <Input placeholder="Enter city" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="postal_postcode" label="Postcode">
            <Input placeholder="+234" defaultValue="+234" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="postal_country" label="Country">
            <Input placeholder="Nigeria" defaultValue="Nigeria" />
          </Form.Item>
        </Col>
      </Row>

      {/* Contact Information - 3 fields per row */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="laboratory_telephone"
            label="1.6 Laboratory Telephone"
            rules={[
              { required: true, message: "Please enter telephone number" },
            ]}
          >
            <Input placeholder="Enter telephone number" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="laboratory_fax" label="1.7 Laboratory Fax">
            <Input placeholder="Enter fax number" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="laboratory_email"
            label="1.8 Laboratory E-mail"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter valid email" },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
        </Col>
      </Row>

      <Divider>1.10 Category of Laboratory</Divider>

      {/* Laboratory Category - 3 fields per row */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="laboratory_level"
            label="Laboratory Level"
            rules={[
              { required: true, message: "Please select laboratory level" },
            ]}
          >
            <Select placeholder="Select level">
              <Option value="tertiary">Tertiary</Option>
              <Option value="secondary">Secondary</Option>
              <Option value="primary">Primary</Option>
              <Option value="reference">Reference / Research</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="laboratory_type"
            label="Laboratory Type"
            rules={[
              { required: true, message: "Please select laboratory type" },
            ]}
          >
            <Select placeholder="Select type">
              <Option value="public">Public</Option>
              <Option value="private">Private</Option>
              <Option value="corporate">Corporate</Option>
              <Option value="faith_based">Faith Based</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="is_international"
            valuePropName="checked"
            style={{ marginTop: 30 }}
          >
            <Checkbox>International Laboratory</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  const renderSchemeSelection = () => (
    <Card title="3.0 Scheme Information" style={{ marginBottom: 24 }}>
      <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
        Select the schemes you wish to enroll in:
      </Text>

      <Form.Item>
        <Checkbox.Group
          value={selectedSchemes}
          onChange={handleSchemeChange}
          style={{ width: "100%" }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Card size="small" style={{ marginBottom: 8 }}>
                <Row gutter={16} align="middle">
                  <Col span={10}>
                    <Text strong>Panel Type</Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Sample & Frequency</Text>
                  </Col>
                  <Col span={4}>
                    <Text strong>Price (₦)</Text>
                  </Col>
                  <Col span={4}>
                    <Text strong>Select</Text>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Regular Schemes */}
            <Col span={24}>
              <Card
                size="small"
                type="inner"
                title="Regular Schemes"
                style={{ marginBottom: 16 }}
              >
                {[
                  {
                    key: "chemistry",
                    name: "Chemistry",
                    sample: "2x2",
                    price: "67,500",
                  },
                  {
                    key: "hematology",
                    name: "Hematology",
                    sample: "2x2",
                    price: "67,500",
                  },
                  {
                    key: "hcg",
                    name: "HCG (Pregnancy Test)",
                    sample: "3x2",
                    price: "20,000",
                  },
                  {
                    key: "hbs_ag",
                    name: "HBs Ag",
                    sample: "3x2",
                    price: "20,000",
                  },
                  {
                    key: "tb_microscopy",
                    name: "T.B Microscopy (10slides)",
                    sample: "10x2",
                    price: "30,000",
                  },
                  {
                    key: "mp_microscopy",
                    name: "MP Microscopy (10slides)",
                    sample: "10x2",
                    price: "30,000",
                  },
                  {
                    key: "hiv_serology",
                    name: "HIV Serology",
                    sample: "3x2",
                    price: "30,000",
                  },
                  { key: "cd4", name: "CD4", sample: "2x2", price: "67,500" },
                  { key: "hcv", name: "HCV", sample: "3x2", price: "20,000" },
                  {
                    key: "h_pylori",
                    name: "H. Pylori",
                    sample: "3x2",
                    price: "20,000",
                  },
                  {
                    key: "malaria_rapid",
                    name: "Malaria Rapid",
                    sample: "3x2",
                    price: "20,000",
                  },
                  {
                    key: "sars_cov_2",
                    name: "SARS-Cov-2",
                    sample: "2x2",
                    price: "150,000",
                  },
                ].map((scheme) => (
                  <Row
                    key={scheme.key}
                    gutter={16}
                    align="middle"
                    style={{ marginBottom: 8 }}
                  >
                    <Col span={10}>
                      <Text>{scheme.name}</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{scheme.sample}</Text>
                    </Col>
                    <Col span={4}>
                      <Text>₦{scheme.price}</Text>
                    </Col>
                    <Col span={4}>
                      <Checkbox value={scheme.key} />
                    </Col>
                  </Row>
                ))}
              </Card>
            </Col>

            {/* BIORAD Schemes */}
            <Col span={24}>
              <Card
                size="small"
                type="inner"
                title="BIORAD Schemes"
                style={{ marginBottom: 16 }}
              >
                {[
                  {
                    key: "immuno_assay_biorad",
                    name: "Immuno Assay- BIORAD",
                    sample: "5ml vial x 12",
                    price: "1,212,200",
                  },
                  {
                    key: "chemistry_biorad",
                    name: "Chemistry - BIORAD",
                    sample: "5ml vial x 12",
                    price: "868,000",
                  },
                  {
                    key: "haematology_biorad",
                    name: "Haematology - BIORAD",
                    sample: "2ml vial x 3",
                    price: "1,212,200",
                  },
                  {
                    key: "courier_biorad",
                    name: "Courier - BIORAD",
                    sample: "Per event shipment",
                    price: "50,000",
                  },
                  {
                    key: "courier",
                    name: "Courier",
                    sample: "Per Cycle (Two Events)",
                    price: "50,000",
                  },
                ].map((scheme) => (
                  <Row
                    key={scheme.key}
                    gutter={16}
                    align="middle"
                    style={{ marginBottom: 8 }}
                  >
                    <Col span={10}>
                      <Text>{scheme.name}</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{scheme.sample}</Text>
                    </Col>
                    <Col span={4}>
                      <Text>₦{scheme.price}</Text>
                    </Col>
                    <Col span={4}>
                      <Checkbox value={scheme.key} />
                    </Col>
                  </Row>
                ))}
              </Card>
            </Col>
          </Row>
        </Checkbox.Group>
      </Form.Item>

      {totalAmount > 0 && (
        <Card
          type="inner"
          style={{ background: "#f6ffed", border: "1px solid #b7eb8f" }}
        >
          <Statistic
            title="Total Amount"
            value={totalAmount}
            prefix="₦"
            valueStyle={{ color: "#389e0d" }}
            formatter={(value) => value.toLocaleString()}
          />
        </Card>
      )}
    </Card>
  );

  const renderReview = () => {
    const values = form.getFieldsValue();

    return (
      <Card title="Review & Submit" style={{ marginBottom: 24 }}>
        <Descriptions
          title="Laboratory Information"
          bordered
          column={2}
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label="Laboratory Name" span={2}>
            {values.laboratory_name}
          </Descriptions.Item>
          <Descriptions.Item label="PML Number">
            {values.pml_number}
          </Descriptions.Item>
          <Descriptions.Item label="CAC Number">
            {values.cac_registration_number || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Laboratory Level">
            {values.laboratory_level}
          </Descriptions.Item>
          <Descriptions.Item label="Laboratory Type">
            {values.laboratory_type}
          </Descriptions.Item>
          <Descriptions.Item label="Telephone">
            {values.laboratory_telephone}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {values.laboratory_email}
          </Descriptions.Item>
          <Descriptions.Item label="Physical Address" span={2}>
            {values.physical_organization}, {values.physical_city},{" "}
            {values.physical_country}
          </Descriptions.Item>
        </Descriptions>

        <Card title="Selected Schemes" style={{ marginBottom: 24 }}>
          {selectedSchemes.length > 0 ? (
            <>
              {selectedSchemes.map((scheme) => (
                <div key={scheme} style={{ marginBottom: 8 }}>
                  <Text>
                    • {scheme.replace(/_/g, " ").toUpperCase()} - ₦
                    {schemePrices[scheme].toLocaleString()}
                  </Text>
                </div>
              ))}
              <Divider />
              <Statistic
                title="Total Amount"
                value={totalAmount}
                prefix="₦"
                valueStyle={{ color: "#1890ff", fontSize: "24px" }}
                formatter={(value) => value.toLocaleString()}
              />
            </>
          ) : (
            <Text type="secondary">No schemes selected</Text>
          )}
        </Card>
      </Card>
    );
  };

  const next = async () => {
    try {
      if (currentStep === 0) {
        await form.validateFields([
          "laboratory_name",
          "pml_number",
          "laboratory_telephone",
          "laboratory_email",
          "laboratory_level",
          "laboratory_type",
        ]);
      }
      setCurrentStep(currentStep + 1);
    } catch (error) {
      message.error("Please fill in all required fields");
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <div
      className="signup_container"
      style={{
        minHeight: "100vh",
        background: `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url(${AppConfig.spiral})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "20px 0",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 24px",
          maxHeight: "calc(100vh - 160px)",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "32px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            marginBottom: "20px",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <Title level={2}>MLSCN/EQA/001 - NEQAL Enrollment Form</Title>
            <Text type="secondary">
              National External Quality Assessment Laboratory Enrollment
            </Text>
          </div>

          <Steps current={currentStep} style={{ marginBottom: 32 }}>
            {steps.map((item) => (
              <Steps.Step
                key={item.title}
                title={item.title}
                icon={item.icon}
              />
            ))}
          </Steps>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              physical_postcode: "+234",
              physical_country: "Nigeria",
              postal_postcode: "+234",
              postal_country: "Nigeria",
            }}
          >
            {currentStep === 0 && renderLaboratoryInfo()}
            {currentStep === 1 && renderSchemeSelection()}
            {currentStep === 2 && renderReview()}

            <div
              style={{
                textAlign: "center",
                marginTop: 24,
                paddingTop: "16px",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Space>
                {currentStep > 0 && (
                  <Button onClick={prev} icon={<ArrowLeftOutlined />}>
                    Previous
                  </Button>
                )}
                {currentStep < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={next}
                    icon={<ArrowRightOutlined />}
                  >
                    Next
                  </Button>
                )}
                {currentStep === steps.length - 1 && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                  >
                    Submit Enrollment
                  </Button>
                )}
              </Space>
            </div>
          </Form>
        </div>

        <small
          className="gx-text-center gx-mt-3 login_desc"
          style={{ display: "block", textAlign: "center", color: "white" }}
        >
          {AppConfig.tagLine}
        </small>
      </div>

      <div
        className="footer_img"
        style={{ textAlign: "center", padding: "20px 0" }}
      >
        <img
          src={AppConfig.spiral}
          alt="spiral design"
          style={{ maxWidth: "100%", height: "auto" }}
        />
      </div>
    </div>
  );
};

export default SignUp;
